// Zustand store para la pantalla nutricionista (Sprint 5 T11).
// Mantiene los 4 listados (ingredientes, platos, dietas, menus) + tab activo
// y expone acciones de workflow (enviar a aprobacion, auto-aprobar Δ3, bloquear).
import { create } from 'zustand'
import {
  nutricionistaApi,
  type AsignarPlatosBody,
  type AutoAprobarBody,
  type BloquearBody,
  type DesbloquearBody,
  type DietaCreateBody,
  type DietaResponse,
  type DietaUpdateBody,
  type EnviarAprobacionBody,
  type IngredienteCreateBody,
  type IngredienteResponse,
  type IngredienteUpdateBody,
  type MenuCreateBody,
  type MenuResponse,
  type PlatoTemplateCreateBody,
  type PlatoTemplateResponse,
  type PlatoTemplateUpdateBody,
  type WorkflowTransicionResponse,
} from '../services/nutricionista'

export type TabKey = 'ingredientes' | 'platos' | 'dietas' | 'menus'

interface NutricionistaCatalogoState {
  ingredientes: IngredienteResponse[]
  platos: PlatoTemplateResponse[]
  dietas: DietaResponse[]
  menus: MenuResponse[]
  loading: boolean
  error: string | null
  tabActivo: TabKey
  ultimaTransicion: WorkflowTransicionResponse | null

  // selectores
  setTabActivo: (tab: TabKey) => void
  limpiarUltimaTransicion: () => void

  // refresco
  refrescarTodo: () => Promise<void>
  refrescarTab: (tab: TabKey) => Promise<void>

  // ingredientes
  crearIngrediente: (body: IngredienteCreateBody) => Promise<IngredienteResponse>
  actualizarIngrediente: (id: number, body: IngredienteUpdateBody) => Promise<IngredienteResponse>
  enviarAprobacionIngrediente: (id: number, body?: EnviarAprobacionBody) => Promise<WorkflowTransicionResponse>
  autoAprobarIngrediente: (id: number, body: AutoAprobarBody) => Promise<WorkflowTransicionResponse>
  bloquearIngrediente: (id: number, body: BloquearBody) => Promise<WorkflowTransicionResponse>
  desbloquearIngrediente: (id: number, body: DesbloquearBody) => Promise<WorkflowTransicionResponse>

  // platos
  crearPlato: (body: PlatoTemplateCreateBody) => Promise<PlatoTemplateResponse>
  actualizarPlato: (id: number, body: PlatoTemplateUpdateBody) => Promise<PlatoTemplateResponse>
  enviarAprobacionPlato: (id: number, body?: EnviarAprobacionBody) => Promise<WorkflowTransicionResponse>
  autoAprobarPlato: (id: number, body: AutoAprobarBody) => Promise<WorkflowTransicionResponse>
  bloquearPlato: (id: number, body: BloquearBody) => Promise<WorkflowTransicionResponse>
  desbloquearPlato: (id: number, body: DesbloquearBody) => Promise<WorkflowTransicionResponse>

  // dietas
  crearDieta: (body: DietaCreateBody) => Promise<DietaResponse>
  actualizarDieta: (id: number, body: DietaUpdateBody) => Promise<DietaResponse>

  // menus
  crearMenu: (body: MenuCreateBody) => Promise<MenuResponse>
  asignarPlatosMenu: (menuId: number, body: AsignarPlatosBody) => Promise<{ insertados: number }>
  activarMenu: (menuId: number) => Promise<MenuResponse>
  archivarMenu: (menuId: number) => Promise<MenuResponse>
}

function extractError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  const maybe = err as { response?: { data?: { detail?: unknown } } }
  const detail = maybe.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object') {
    const obj = detail as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.code === 'string') return obj.code
  }
  return 'Error desconocido'
}

// El backend pagina (max 200 por request) y el catalogo ya supera esa cifra
// (416 alimentos, 4-jul). Cargar una sola pagina ocultaba parte del catalogo
// y la nutricionista creaba duplicados de lo que no veia — traer TODAS las
// paginas hasta recibir una incompleta.
const PAGE_LIMIT = 200

async function listarTodasLasPaginas<T>(
  fetchPage: (page: number) => Promise<T[]>,
): Promise<T[]> {
  const todos: T[] = []
  for (let page = 1; ; page++) {
    const lote = await fetchPage(page)
    todos.push(...lote)
    if (lote.length < PAGE_LIMIT) break
  }
  return todos
}

const listarIngredientesCompleto = () =>
  listarTodasLasPaginas((page) =>
    nutricionistaApi.listarIngredientes({ page, limit: PAGE_LIMIT }),
  )

const listarPlatosCompleto = () =>
  listarTodasLasPaginas((page) => nutricionistaApi.listarPlatos(page, PAGE_LIMIT))

export const useNutricionistaCatalogoStore = create<NutricionistaCatalogoState>((set, get) => ({
  ingredientes: [],
  platos: [],
  dietas: [],
  menus: [],
  loading: false,
  error: null,
  tabActivo: 'ingredientes',
  ultimaTransicion: null,

  setTabActivo: (tab) => set({ tabActivo: tab }),
  limpiarUltimaTransicion: () => set({ ultimaTransicion: null }),

  refrescarTodo: async () => {
    set({ loading: true, error: null })
    try {
      const [ingredientes, platos, dietas, menus] = await Promise.all([
        listarIngredientesCompleto(),
        listarPlatosCompleto(),
        nutricionistaApi.listarDietas(),
        nutricionistaApi.listarMenus(),
      ])
      set({ ingredientes, platos, dietas, menus, loading: false })
    } catch (err) {
      set({ loading: false, error: extractError(err) })
    }
  },

  refrescarTab: async (tab) => {
    set({ loading: true, error: null })
    try {
      if (tab === 'ingredientes') {
        const ingredientes = await listarIngredientesCompleto()
        set({ ingredientes })
      } else if (tab === 'platos') {
        const platos = await listarPlatosCompleto()
        set({ platos })
      } else if (tab === 'dietas') {
        const dietas = await nutricionistaApi.listarDietas()
        set({ dietas })
      } else if (tab === 'menus') {
        const menus = await nutricionistaApi.listarMenus()
        set({ menus })
      }
      set({ loading: false })
    } catch (err) {
      set({ loading: false, error: extractError(err) })
    }
  },

  // ----- Ingredientes -----
  crearIngrediente: async (body) => {
    const item = await nutricionistaApi.crearIngrediente(body)
    set((s) => ({ ingredientes: [item, ...s.ingredientes] }))
    return item
  },
  actualizarIngrediente: async (id, body) => {
    const item = await nutricionistaApi.actualizarIngrediente(id, body)
    set((s) => ({
      ingredientes: s.ingredientes.map((it) => (it.id === id ? item : it)),
    }))
    return item
  },
  enviarAprobacionIngrediente: async (id, body = {}) => {
    const res = await nutricionistaApi.enviarAprobacionIngrediente(id, body)
    set({ ultimaTransicion: res })
    await get().refrescarTab('ingredientes')
    return res
  },
  autoAprobarIngrediente: async (id, body) => {
    const res = await nutricionistaApi.autoAprobarIngrediente(id, body)
    set({ ultimaTransicion: res })
    await get().refrescarTab('ingredientes')
    return res
  },
  bloquearIngrediente: async (id, body) => {
    const res = await nutricionistaApi.bloquearIngrediente(id, body)
    set({ ultimaTransicion: res })
    await get().refrescarTab('ingredientes')
    return res
  },
  desbloquearIngrediente: async (id, body) => {
    const res = await nutricionistaApi.desbloquearIngrediente(id, body)
    set({ ultimaTransicion: res })
    await get().refrescarTab('ingredientes')
    return res
  },

  // ----- Platos -----
  crearPlato: async (body) => {
    const item = await nutricionistaApi.crearPlato(body)
    set((s) => ({ platos: [item, ...s.platos] }))
    return item
  },
  actualizarPlato: async (id, body) => {
    const item = await nutricionistaApi.actualizarPlato(id, body)
    set((s) => ({
      platos: s.platos.map((it) => (it.id === id ? item : it)),
    }))
    return item
  },
  enviarAprobacionPlato: async (id, body = {}) => {
    const res = await nutricionistaApi.enviarAprobacionPlato(id, body)
    set({ ultimaTransicion: res })
    await get().refrescarTab('platos')
    return res
  },
  autoAprobarPlato: async (id, body) => {
    const res = await nutricionistaApi.autoAprobarPlato(id, body)
    set({ ultimaTransicion: res })
    await get().refrescarTab('platos')
    return res
  },
  bloquearPlato: async (id, body) => {
    const res = await nutricionistaApi.bloquearPlato(id, body)
    set({ ultimaTransicion: res })
    await get().refrescarTab('platos')
    return res
  },
  desbloquearPlato: async (id, body) => {
    const res = await nutricionistaApi.desbloquearPlato(id, body)
    set({ ultimaTransicion: res })
    await get().refrescarTab('platos')
    return res
  },

  // ----- Dietas -----
  crearDieta: async (body) => {
    const item = await nutricionistaApi.crearDieta(body)
    set((s) => ({ dietas: [...s.dietas, item].sort((a, b) => a.codigo.localeCompare(b.codigo)) }))
    return item
  },
  actualizarDieta: async (id, body) => {
    const item = await nutricionistaApi.actualizarDieta(id, body)
    set((s) => ({
      dietas: s.dietas.map((d) => (d.id === id ? item : d)),
    }))
    return item
  },

  // ----- Menus -----
  crearMenu: async (body) => {
    const item = await nutricionistaApi.crearMenu(body)
    set((s) => ({ menus: [item, ...s.menus] }))
    return item
  },
  asignarPlatosMenu: async (menuId, body) => {
    const res = await nutricionistaApi.asignarPlatosMenu(menuId, body)
    return res
  },
  activarMenu: async (menuId) => {
    const item = await nutricionistaApi.activarMenu(menuId)
    set((s) => ({ menus: s.menus.map((m) => (m.id === menuId ? item : m)) }))
    return item
  },
  archivarMenu: async (menuId) => {
    const item = await nutricionistaApi.archivarMenu(menuId)
    set((s) => ({ menus: s.menus.map((m) => (m.id === menuId ? item : m)) }))
    return item
  },
}))
